import React from "react";
import {Box, Chip, IconButton, MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import {AddOutlined, ChevronLeft, ChevronRight, CloseOutlined, ExpandLess, ExpandMore,
        HelpOutlineOutlined} from "@mui/icons-material";
import FieldRoleHelp from "./FieldRoleHelp";

import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {FILTER_OPERATORS, RELATIVE_UNITS, TABLE_CALCULATIONS} from "./querySpec";
import {Shelf, Shelves, aggregationsFor} from "./buildQuery";
import {Calculation} from "./querySpec";
import CalculationEditor from "./CalculationEditor";
import {Dataset, DatasetField} from "./reportApi";
import {BUILDER_COLUMN} from "./builderLayout";

/**
 * The arrangement half of the builder: a field list, two shelves and the filters.
 *
 * CLICK TO ADD, NOT DRAG. QuickSight, Tableau and Power BI are drag-and-drop, and this is not --
 * deliberately, and it is worth saying why rather than letting it read as unfinished. Drag needs a
 * library we cannot load (the app's CDN allowlist aside, it is a dependency to own forever), and
 * every drag interface also has a click path because drag is unusable on a touch screen and
 * unreachable with a keyboard. So the click path is what got built. The arrangement it produces is
 * identical, and drag can be added over it later without changing what a report MEANS.
 *
 * WHICH SHELF A FIELD MAY GO ON IS DECIDED FROM ITS TYPE, not offered and then refused. A measure
 * offers sums and averages; a dimension offers counts, because counting a dimension -- "how many
 * per department" -- is the thing people reach for first and refusing it would be pedantry. The
 * rules live in buildQuery, where they can be tested without a page.
 */


type Props = {
    dataset: Dataset | undefined;
    shelves: Shelves;
    onChange: (shelves: Shelves) => void;
};

const LABEL = {fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
               textTransform: "uppercase" as const, color: Blue3, mb: 0.75};

const ARROWS = {left: ChevronLeft, right: ChevronRight, up: ExpandLess, down: ExpandMore};

/**
 * One nudge along a shelf.
 *
 * titleAccess, NOT aria-label. MUI's SvgIcon sets aria-hidden on every icon unless it is given a
 * title, so a label on the icon is in the DOM and absent from the accessibility tree -- which this
 * app has now shipped three times and this suite has caught three times. The name goes on the
 * BUTTON; the icon is told it is decoration.
 *
 * The span is what carries a tooltip over a disabled control, which MUI cannot do directly.
 */
function Arrow({which, label, disabled, onClick}: {
    which: keyof typeof ARROWS; label: string; disabled: boolean; onClick: () => void;
}) {
    const Icon = ARROWS[which];
    return (
        <span>
            <IconButton size="small" aria-label={label} disabled={disabled} onClick={onClick}
                        sx={{p: 0, width: 16, height: 16}}>
                <Icon aria-hidden sx={{fontSize: 15, color: disabled ? "#E0E3E8" : Grey6}}/>
            </IconButton>
        </span>
    );
}

function Section({title, note, above, children}:
                 {title: string; note?: string;
                  /** Extra space above this section, in px. Sections are evenly spaced by default;
                   *  Filters takes some because it is a different KIND of thing from the two
                   *  shelves above it -- those say what the result is made of, and this narrows
                   *  what goes into it. The gap is what says so. */
                  above?: number; children: React.ReactNode}) {
    return (
        <Box sx={{mb: 2.5, ...(above ? {mt: `${above}px`} : {})}}>
            {/* THE HEADING IS SET APART FROM ITS OWN NOTE, and the note from what it introduces.
                LABEL's own mb is the spacing between a heading and a CONTROL; a paragraph needs
                more, or the three sizes of text here read as one block and the heading stops
                looking like a heading. Overridden here rather than in LABEL, which other headings
                sit directly on top of their controls with. */}
            <Typography sx={{...LABEL, mb: 1.25}}>{title}</Typography>
            {note ? <Typography sx={{fontSize: 12, color: Grey6, mb: 2}}>{note}</Typography> : null}
            {children}
        </Box>
    );
}

export default function ReportShelves({dataset, shelves, onChange}: Props) {
    const [helpRole, setHelpRole] = React.useState<string | null>(null);

    // WHAT ONE ROW IS CHANGES WHAT EVERY SHELF MEANS. In a listing a dimension is only a column --
    // it no longer decides what a row is, because a row is a record -- and there is nothing for a
    // measure to summarize over. Saying so in the headings is better than saying it in the docs.
    const listing = shelves.shape === "detail";

    const [calculating, setCalculating] = React.useState(false);
    const fields = dataset?.Fields ?? [];
    const fieldOf = (code: string) => fields.find(one => one.Field === code);

    const addTo = (where: "rows" | "values", field: DatasetField) => {
        const shelf: Shelf = where === "values"
            ? {field: field.Field, aggregation: aggregationsFor(field)[0]?.id}
            : {field: field.Field};
        onChange({...shelves, [where]: [...shelves[where], shelf]});
    };

    const removeFrom = (where: "rows" | "values", index: number) =>
        onChange({...shelves, [where]: shelves[where].filter((_one, at) => at !== index)});

    /**
     * Move one field along its shelf.
     *
     * SHELF ORDER IS COLUMN ORDER -- it decides the SELECT list and, in a summary, the GROUP BY --
     * so getting it wrong used to mean taking every field off and putting them back in the order
     * you wanted. That is a five-click fix for a one-place mistake, and it loses each measure's
     * aggregation on the way.
     *
     * ARROWS RATHER THAN DRAGGING. Dragging is what this class of tool usually offers and it is
     * the richer answer, but it needs a library in a CRA app pinned this carefully, and it is
     * reachable by pointer only -- in a file that has already shipped three controls a screen
     * reader could not see. These are ordinary buttons with ordinary names.
     */
    const move = (where: "rows" | "values", index: number, by: -1 | 1) =>
        reorder(where, index, index + by);

    /** Take the field at `from` off the shelf and put it back at `to`. */
    const reorder = (where: "rows" | "values", from: number, to: number) => {
        const shelf = shelves[where];
        if (to < 0 || to >= shelf.length || from === to) return;
        const next = [...shelf];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onChange({...shelves, [where]: next});
    };

    /**
     * Dragging, on top of the arrows rather than instead of them.
     *
     * NATIVE HTML5 DRAG EVENTS, no library. A drag-and-drop package is a real dependency in a CRA
     * app pinned this carefully, and what it would buy over forty lines here is animation.
     *
     * THE ARROWS STAY because dragging is pointer-only. They are transparent until the chip is
     * hovered or something inside it takes focus -- opacity rather than visibility or display, so
     * they keep their place in the layout AND in the accessibility tree. A keyboard reaches them
     * by tabbing, which is when they appear.
     *
     * WITHIN ONE SHELF. Dragging a dimension onto Summarize looks like it should work and would
     * have to invent an aggregation for it, so a drag that leaves its own shelf does nothing.
     */
    const [dragging, setDragging] = React.useState<{where: string; index: number} | null>(null);
    const [over, setOver] = React.useState<{where: string; index: number} | null>(null);

    const dragProps = (where: "rows" | "values", index: number) => ({
        draggable: true,
        onDragStart: (event: React.DragEvent) => {
            setDragging({where, index});
            event.dataTransfer.effectAllowed = "move";
            // FIREFOX WILL NOT START A DRAG without something on the dataTransfer. The payload is
            // never read -- the indices are in state, because what is being dragged is a position
            // on a shelf and not a value anybody could usefully drop elsewhere.
            event.dataTransfer.setData("text/plain", String(index));
        },
        onDragOver: (event: React.DragEvent) => {
            if (dragging?.where !== where) return;
            // preventDefault IS WHAT MAKES A DROP LEGAL. Without it the browser refuses every
            // drop and the drag ends by animating back, with no error anywhere.
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            if (over?.index !== index || over?.where !== where) setOver({where, index});
        },
        onDrop: (event: React.DragEvent) => {
            event.preventDefault();
            if (dragging?.where === where) reorder(where, dragging.index, index);
            setDragging(null);
            setOver(null);
        },
        onDragEnd: () => { setDragging(null); setOver(null); },
    });

    /** How a chip or row looks while a drag is happening over or from it. */
    const dragStyle = (where: "rows" | "values", index: number) => ({
        cursor: "grab",
        opacity: dragging?.where === where && dragging.index === index ? 0.4 : 1,
        // WHERE IT WILL LAND, drawn on the field it would displace. A drag with no indicator is a
        // guess, and the guess is wrong about half the time at the ends.
        ...(over?.where === where && over.index === index && dragging?.index !== index
            ? where === "rows"
                ? {boxShadow: `inset 3px 0 0 ${Blue3}`}
                : {boxShadow: `inset 0 3px 0 ${Blue3}`}
            : {}),
    });

    const setAggregation = (index: number, aggregation: string) =>
        onChange({...shelves,
                  values: shelves.values.map((one, at) => at === index ? {...one, aggregation} : one)});

    const addFilter = () => {
        const first = fields[0];
        if (!first) return;
        onChange({...shelves,
                  filters: [...shelves.filters,
                            {of: {dataset: shelves.dataset, field: first.Field}, operator: "equals",
                             value: ""}]});
    };

    const setFilter = (index: number, over: Record<string, unknown>) =>
        onChange({...shelves,
                  filters: shelves.filters.map((one, at) => at === index ? {...one, ...over} : one)});

    const removeFilter = (index: number) =>
        onChange({...shelves, filters: shelves.filters.filter((_one, at) => at !== index)});

    return (
        <Box sx={{display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap"}}>
            <FieldRoleHelp role={helpRole} onClose={() => setHelpRole(null)}/>

            {/* THE FIELD LIST, split by role. Not one alphabetical list with a type icon: the split
                is the thing a reader has to understand to use the tool at all, and a legend is a
                worse way to teach it than an arrangement. */}
            <Box sx={{width: BUILDER_COLUMN, flexShrink: 0}}>
                <Section title="Fields"
                         note={dataset
                             ? "Put a field on a shelf and it appears in the result. There is no separate step for choosing columns."
                             : "Choose a dataset first"}>
                    {(["dimension", "measure"] as const).map(role => {
                        const group = fields.filter(one => one.Role === role);

                        // AN EMPTY MEASURES HEADING STILL APPEARS, and that is the point of it.
                        //
                        // Hiding the heading when a form has no numeric fields removed the one
                        // place that explains WHY -- the help beside it says "a field is a measure
                        // because it is a Number on the form it came from", which is exactly the
                        // sentence somebody needs when they cannot find anything to summarize. The
                        // explanation disappeared precisely in the case that prompts the question,
                        // and the reader was left to conclude the feature was broken. Ed asked it
                        // of this very screen.
                        //
                        // Dimensions keep the old behaviour: every dataset has some, and a heading
                        // over nothing would be noise rather than an answer.
                        const empty = !group.length;
                        if (empty && (role !== "measure" || !dataset)) return null;

                        // ORIGIN IS A COLUMN, NOT A SECOND GROUPING, and that is a correction.
                        //
                        // It was nested sub-headings -- On the record / On this form inside each
                        // role -- which put THREE levels of heading into a 250px column, and the
                        // outer one ("Group by") repeated the name of the shelf beside it. The
                        // arrangement that teaches a two-way split stops teaching anything when it
                        // is the third such split on screen.
                        //
                        // So each row states its own origin and nothing has to be held in the
                        // reader's head. Every field carries the word, including "record": marking
                        // only one kind makes the absence of a mark meaningful, which is a rule
                        // nobody is told.
                        return (
                            <Box key={role} sx={{mb: 1.5}}>
                                {/* DIMENSIONS / MEASURES, which is what QuickSight, Tableau and
                                    PowerBI all call them -- and, more to the point, names the kind
                                    of field instead of repeating the verb on the shelf next to it.
                                    "Group by" appearing twice on one screen meaning two different
                                    things is the confusion this removes. */}
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 0.5}}>
                                    <Typography sx={{fontSize: 11, color: Grey6, lineHeight: 1,
                                                     textTransform: "uppercase", letterSpacing: "0.4px"}}>
                                        {role === "dimension" ? "Dimensions" : "Measures"}
                                    </Typography>
                                    {/* A DRAWER, NOT A TOOLTIP. Five paragraphs in a hover card is
                                        a paragraph nobody finishes: it has to be held open by the
                                        pointer, cannot be scrolled, and vanishes the moment
                                        somebody looks back at the shelves to apply what they read. */}
                                    <IconButton size="small"
                                                aria-label={role === "dimension"
                                                    ? "What dimensions are for"
                                                    : "What measures are for"}
                                                onClick={() => setHelpRole(role)}
                                                sx={{p: 0.25, color: Blue3,
                                                     "&:hover": {color: Grey17}}}>
                                        <HelpOutlineOutlined sx={{fontSize: 15, display: "block"}}/>
                                    </IconButton>
                                </Box>
                                {empty ? (
                                    <Typography sx={{fontSize: 12, color: Grey5, mb: 0.5}}>
                                        No numeric fields on this form, so there is nothing to
                                        total. A field is a measure because its form asks for a
                                        Number.
                                    </Typography>
                                ) : null}
                                {group.map(field => (
                                    <Box key={field.Field}
                                         sx={{display: "flex", alignItems: "center", gap: 0.5,
                                              py: 0.25}}>
                                        <Typography sx={{fontSize: 13, color: Grey17, flexGrow: 1,
                                                         overflow: "hidden", textOverflow: "ellipsis",
                                                         whiteSpace: "nowrap"}}
                                                    title={field.Retired ? "Retired: no longer asked, past answers kept" : field.Label}>
                                            {field.Label}{field.Retired ? " ·" : ""}
                                        </Typography>
                                        {/* WHERE IT CAME FROM, on the row itself. "record" is a
                                            column every record of every type has, whose meaning is
                                            the platform's and which nobody can take away; "form"
                                            is an answer somebody's form design asks for, and
                                            whoever owns that form can retire or rename it.
                                            Identical to query; different promises. */}
                                        <Typography sx={{fontSize: 10, color: Grey6, flexShrink: 0,
                                                         fontStyle: "italic"}}
                                                    title={field.Origin === "record"
                                                        ? "On the record: every record of every type has this"
                                                        : "On this form: an answer this type's form asks for"}>
                                            {field.Origin === "record" ? "record" : "form"}
                                        </Typography>
                                        {/* NAMED, AND NAMED WITH THE FIELD. These two are the
                                            primary interaction of the whole app and they were
                                            unlabelled icons -- a Tooltip title is not an accessible
                                            name, so a screen reader announced "button, button" per
                                            row and nothing could find them by role. Carrying the
                                            field's own label also makes "add Department" and "add
                                            Review Period" distinguishable, which matters when there
                                            are twelve of each on screen. */}
                                        <Tooltip title={listing ? "Add this as a column" : "Group by this"}>
                                            <span>
                                                <IconButton size="small"
                                                            aria-label={`${listing ? "Add" : "Group by"} ${field.Label}`}
                                                            onClick={() => addTo("rows", field)}>
                                                    <AddOutlined sx={{fontSize: 15, color: Grey6}}/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {/* DISABLED, NOT REMOVED, and the tooltip says which mode
                                            it belongs to. The span is what carries the tooltip when
                                            the button is disabled -- MUI cannot hang one on a
                                            disabled control, which is why every one of these is
                                            already wrapped. */}
                                        <Tooltip title={listing
                                            ? "Summarizing needs one row per group"
                                            : role === "measure" ? "Summarize this" : "Count this"}>
                                            <span>
                                                <IconButton size="small" disabled={listing}
                                                            aria-label={`${role === "measure" ? "Summarize" : "Count"} ${field.Label}`}
                                                            onClick={() => addTo("values", field)}>
                                                    <Typography sx={{fontSize: 11,
                                                                     color: listing ? "#C3C7CF" : Grey6}}>Σ</Typography>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}
                </Section>
            </Box>

            <Box sx={{flexGrow: 1, minWidth: 320}}>
                {/* THE NOTE SAYS ONLY WHAT THE SHELF DOES. It sits directly under the One row per
                    control, so "One row per record" here restated the control's own label two
                    inches below it and read as though it were adding something. What is left is
                    the difference between the modes and nothing else: one shelf makes columns,
                    the other makes columns AND cuts the rows. */}
                <Section title={listing ? "Columns" : "Group by"}
                         note={listing
                             ? "Each becomes a column of the result."
                             : "Each becomes a column, and you get one row per combination of them. A date can be grouped by part."}>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 34}}>
                        {shelves.rows.length === 0
                            ? <Typography sx={{fontSize: 12.5, color: Grey6}}>Nothing yet</Typography>
                            : shelves.rows.map((shelf, index) => (
                                <Chip key={`${shelf.field}-${index}`} size="small"
                                      {...dragProps("rows", index)}
                                      label={fieldOf(shelf.field)?.Label ?? shelf.field}
                                      // NAMED, BECAUSE MUI MAKES THE CHIP ITSELF A BUTTON as soon
                                      // as it has onDelete -- focusable, and deletable with the
                                      // Delete key, which is worth having. Unnamed, its accessible
                                      // name is computed from its contents, so the moment the
                                      // arrows moved inside it began announcing itself as "Move
                                      // Department left Move Department right Department Remove
                                      // Department". A screen reader reads that aloud; the suite
                                      // found it as two buttons answering to one name.
                                      aria-label={fieldOf(shelf.field)?.Label ?? shelf.field}
                                      // INSIDE THE CHIP, NOT BESIDE IT. Sat outside, one field's
                                      // "right" ended up touching the next field's "left" and the
                                      // row read "› ‹" between every pair -- four arrows in a gap,
                                      // none of them obviously belonging to anything. In the icon
                                      // slot the pair is unambiguously this chip's.
                                      icon={
                                          <Box className="shelf-arrows"
                                               sx={{display: "flex", alignItems: "center", ml: 0.25,
                                                    // TRANSPARENT, NOT HIDDEN. opacity keeps them
                                                    // in the layout and in the accessibility tree,
                                                    // so a keyboard still reaches them -- and
                                                    // focus-within is what makes them visible when
                                                    // it does.
                                                    opacity: 0, transition: "opacity 120ms"}}>
                                              {/* ONLY WHERE IT CAN GO: the first field has no
                                                  "left" and the last no "right". A button that is
                                                  present and does nothing is one somebody presses
                                                  twice before believing it. */}
                                              <Arrow which="left" disabled={index === 0}
                                                     label={`Move ${fieldOf(shelf.field)?.Label ?? shelf.field} left`}
                                                     onClick={() => move("rows", index, -1)}/>
                                              <Arrow which="right"
                                                     disabled={index === shelves.rows.length - 1}
                                                     label={`Move ${fieldOf(shelf.field)?.Label ?? shelf.field} right`}
                                                     onClick={() => move("rows", index, 1)}/>
                                          </Box>
                                      }
                                      onDelete={() => removeFrom("rows", index)}
                                      // NAMED, AND REACHABLE. MUI's delete icon ships with no
                                      // accessible name -- the third time this app has shipped that
                                      // exact gap, and the reason the two before it are in the
                                      // front-door suite.
                                      //
                                      // titleAccess RATHER THAN aria-label, which was tried first
                                      // and did nothing: SvgIcon sets aria-hidden on every icon
                                      // unless it is given one, so the label was present in the DOM
                                      // and absent from the accessibility tree. A screen reader saw
                                      // nothing; so did getByRole, which is how it was caught.
                                      deleteIcon={<CloseOutlined titleAccess={`Remove ${fieldOf(shelf.field)?.Label ?? shelf.field}`}
                                                                 role="button"
                                                                 sx={{fontSize: 14}}/>}
                                      sx={{fontSize: 12, borderRadius: "4px",
                                           backgroundColor: "rgba(20,152,216,0.12)",
                                           ...dragStyle("rows", index),
                                           "&:hover .shelf-arrows, &:focus-within .shelf-arrows":
                                               {opacity: 1},
                                           "&:active": {cursor: "grabbing"}}}/>
                            ))}
                    </Box>
                </Section>

                <Section title="Summarize"
                         note={listing
                             ? "Not for a listing: there are no groups to total. Switch to one row per group to summarize."
                             : "Each becomes a column too, holding that group's total. A text field can only be counted."}>
                    {/* GREYED WITH ITS REASON RATHER THAN HIDDEN. A shelf that vanishes reads as a
                        tool that cannot do the thing; one that is visibly unavailable, and says
                        why, reads as a tool that can -- in the other mode. */}
                    {listing && shelves.values.length === 0
                        ? null
                        : shelves.values.length === 0
                        ? <Typography sx={{fontSize: 12.5, color: Grey6}}>Nothing yet</Typography>
                        : shelves.values.map((shelf, index) => (
                            <Box key={`${shelf.field}-${index}`}
                                 {...dragProps("values", index)}
                                 sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.75,
                                      borderRadius: "4px",
                                      ...dragStyle("values", index),
                                      "&:hover .shelf-arrows, &:focus-within .shelf-arrows":
                                          {opacity: 1},
                                      "&:active": {cursor: "grabbing"}}}>
                                {/* UP AND DOWN HERE, because this shelf is a column of rows and
                                    left/right would name a direction that is not on the screen. */}
                                <Box className="shelf-arrows"
                                     sx={{display: "flex", flexDirection: "column",
                                          opacity: 0, transition: "opacity 120ms"}}>
                                    <Arrow which="up" disabled={index === 0}
                                           label={`Move ${shelf.calculation?.label ?? fieldOf(shelf.field)?.Label ?? shelf.field} up`}
                                           onClick={() => move("values", index, -1)}/>
                                    <Arrow which="down" disabled={index === shelves.values.length - 1}
                                           label={`Move ${shelf.calculation?.label ?? fieldOf(shelf.field)?.Label ?? shelf.field} down`}
                                           onClick={() => move("values", index, 1)}/>
                                </Box>
                                <TextField select size="small" sx={{width: 150}}
                                           value={shelf.aggregation ?? ""}
                                           onChange={event => setAggregation(index, event.target.value)}>
                                    {aggregationsFor(shelf.calculation
                                        ? {Field: "", Label: "", Role: "measure"}
                                        : fieldOf(shelf.field)).map(one => (
                                        <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                                    ))}
                                </TextField>
                                <Typography sx={{fontSize: 13, color: Grey17}}
                                            title={shelf.calculation ? "A calculation" : undefined}>
                                    {shelf.calculation
                                        ? `ƒ ${shelf.calculation.label}`
                                        : fieldOf(shelf.field)?.Label ?? shelf.field}
                                </Typography>
                                {/* A WINDOW OVER THE FINISHED ROWS, offered per measure. "Show as"
                                    rather than "table calculation", because the second is a phrase
                                    from a manual and the first is what somebody is asking for. */}
                                {/* displayEmpty, OR THE DEFAULT IS INVISIBLE. The first entry is a
                                    real choice -- "Show as: the value" -- and it carries the empty
                                    string, because that is what "no table calculation" is in the
                                    specification. MUI renders an empty value as a BLANK box unless
                                    told otherwise, so the control looked unset beside the
                                    aggregation next to it, which shows "Count". It was never
                                    missing a default; the default could not be seen. */}
                                {/* 340, DOUBLE. Every entry here is a SENTENCE -- "Show as: the
                                    value", "Show as: a running total" -- and 170px truncated all
                                    of them to "Show as: the va…", which hides the only part that
                                    differs between the options. A control whose choices are
                                    indistinguishable is not a choice. */}
                                <TextField select size="small" sx={{width: 340}}
                                           slotProps={{select: {displayEmpty: true}}}
                                           value={shelf.tableCalculation ?? ""}
                                           onChange={event => onChange({...shelves,
                                               values: shelves.values.map((one, at) => at === index
                                                   ? {...one, tableCalculation: event.target.value || undefined}
                                                   : one)})}>
                                    <MenuItem value="">Show as: the value</MenuItem>
                                    {TABLE_CALCULATIONS.filter(one => one.phase === "v1").map(one => (
                                        <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                                    ))}
                                </TextField>
                                {/* SCOPED TO A GROUP, which is level-of-detail in the form people
                                    reach for: each row against its own region rather than against
                                    everything. Only offered once the calculation is one, and only
                                    over dimensions the report actually slices by. */}
                                {shelf.tableCalculation && shelves.rows.length > 1 ? (
                                    <TextField select size="small" sx={{width: 180}}
                                               slotProps={{select: {displayEmpty: true}}}
                                               value={(shelf.within ?? [])[0] ?? ""}
                                               onChange={event => onChange({...shelves,
                                                   values: shelves.values.map((one, at) => at === index
                                                       ? {...one, within: event.target.value
                                                           ? [event.target.value] : undefined}
                                                       : one)})}>
                                        <MenuItem value="">of everything</MenuItem>
                                        {shelves.rows.map(row => (
                                            <MenuItem key={row.field} value={row.field}>
                                                within {fieldOf(row.field)?.Label ?? row.field}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : null}
                                <IconButton size="small" onClick={() => removeFrom("values", index)}>
                                    <CloseOutlined sx={{fontSize: 15, color: Grey6}}/>
                                </IconButton>
                            </Box>
                        ))}
                    {listing ? null : (
                        <Typography onClick={() => setCalculating(true)}
                                    sx={{fontSize: 12.5, color: Blue3, cursor: "pointer", mt: 0.5}}>
                            + Add a calculation
                        </Typography>
                    )}
                </Section>

                <Section title="Filters" above={20}
                         note={listing
                             ? "Applied to each record. The same words a quality rule uses."
                             : "Applied before the grouping. The same words a quality rule uses."}>
                    {shelves.filters.map((filter, index) => (
                        <Box key={index} sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.75,
                                              flexWrap: "wrap"}}>
                            <TextField select size="small" sx={{width: 190}}
                                       value={filter.of?.field ?? ""}
                                       onChange={event => setFilter(index, {
                                           of: {dataset: shelves.dataset, field: event.target.value}})}>
                                {fields.map(one => (
                                    <MenuItem key={one.Field} value={one.Field}>{one.Label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField select size="small" sx={{width: 160}} value={filter.operator}
                                       onChange={event => setFilter(index, {operator: event.target.value})}>
                                {FILTER_OPERATORS.filter(one => one.phase === "v1").map(one => (
                                    <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                                ))}
                            </TextField>
                            {/* A value is not asked for when the operator does not take one --
                                offering an empty box beside "is blank" asks a question with no
                                answer and invites somebody to type into it. */}
                            {["isBlank", "isNotBlank"].includes(filter.operator) ? null
                              : filter.operator === "relativeDate" ? (
                                <>
                                    {/* A NUMBER AND A PERIOD. "The last 30 days" is worked out when
                                        the report RUNS -- saved as two dates it would stop meaning
                                        what it said the following day. */}
                                    <TextField size="small" sx={{width: 90}} placeholder="30"
                                               value={String(filter.value ?? "")}
                                               onChange={event => setFilter(index, {
                                                   value: event.target.value.replace(/[^0-9]/g, "")})}/>
                                    <TextField select size="small" sx={{width: 120}}
                                               value={(filter as any).unit ?? "day"}
                                               onChange={event => setFilter(index, {unit: event.target.value})}>
                                        {RELATIVE_UNITS.filter(one => one.phase === "v1").map(one => (
                                            <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            ) : (
                                <TextField size="small" sx={{width: 190}} placeholder="value"
                                           value={String(filter.value ?? "")}
                                           onChange={event => setFilter(index, {value: event.target.value})}/>
                            )}
                            <IconButton size="small" onClick={() => removeFilter(index)}>
                                <CloseOutlined sx={{fontSize: 15, color: Grey6}}/>
                            </IconButton>
                        </Box>
                    ))}
                    <Typography onClick={addFilter}
                                sx={{fontSize: 12.5, color: Blue3, cursor: "pointer", mt: 0.5}}>
                        + Add a filter
                    </Typography>
                </Section>
            </Box>
            <CalculationEditor open={calculating} fields={fields}
                               onCancel={() => setCalculating(false)}
                               onSave={(calculation: Calculation) => {
                                   setCalculating(false);
                                   // A calculation lands on the values shelf with an aggregation,
                                   // because it is a number per row like any other measure.
                                   onChange({...shelves,
                                             values: [...shelves.values,
                                                      {field: "", calculation, aggregation: "avg"}]});
                               }}/>
        </Box>
    );
}

export {Grey5};
